import { useState, useCallback, useMemo } from 'react';
import { useGetList } from '@/queries/admin';
import { ApiResource } from '@/types/admin-api';

export const useResourceList = <R extends ApiResource>(resource: R) => {
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filter, setFilter] = useState('');

  const handleFilterChange = useCallback((value: string) => {
    setFilter(value);
    setPage(1);
  }, []);

  const handleSortChange = useCallback(
    (newSortBy: string) => {
      if (newSortBy === sortBy) {
        setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
      } else {
        setSortBy(newSortBy);
        setSortOrder('ASC');
      }
      setPage(1);
    },
    [sortBy, sortOrder],
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleLimitChange = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  }, []);

  const params = useMemo(
    () => ({
      sortBy,
      sortOrder,
      page,
      limit,
      title: filter,
    }),
    [sortBy, sortOrder, page, limit, filter],
  );

  const query = useGetList(resource, params);
  const totalItems = useMemo(() => query.data?.total || 0, [query.data]);
  const totalPages = useMemo(() => Math.ceil(totalItems / limit), [totalItems, limit]);

  return {
    ...query,

    sortBy,
    sortOrder,
    page,
    limit,
    filter,
    totalItems,
    totalPages,
    handleFilterChange,
    handleSortChange,
    handlePageChange,
    handleLimitChange,

    reset: () => {
      setSortBy('id');
      setSortOrder('ASC');
      setPage(1);
      setLimit(10);
      setFilter('');
    },
  };
};
